import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent } from "@/components/ui/card";
import { Pill, Package, Loader2, FileDown, Sparkles, CalendarDays, ClipboardList } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";

interface Prescription {
  id: string;
  peptide_name: string;
  dosage: string;
  status: string;
  created_at: string;
}

interface Order {
  id: string;
  amount_cents: number;
  status: string;
  order_type: string;
  created_at: string;
  batch_number: string | null;
  coa_document_url: string | null;
}

const statusMap: Record<string, { label: string; variant: "default" | "secondary" | "destructive" | "outline" }> = {
  draft: { label: "Preparing", variant: "secondary" },
  sent: { label: "Sent to Pharmacy", variant: "default" },
  filled: { label: "Filled", variant: "default" },
  shipped: { label: "Shipped", variant: "default" },
  pending: { label: "Processing", variant: "secondary" },
  paid: { label: "Paid", variant: "default" },
  completed: { label: "Completed", variant: "default" },
  cancelled: { label: "Cancelled", variant: "destructive" },
};

const TreatmentStatus = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [prescriptions, setPrescriptions] = useState<Prescription[]>([]);
  const [orders, setOrders] = useState<Order[]>([]);
  const [hasAssessment, setHasAssessment] = useState(false);
  const [hasUpcomingAppt, setHasUpcomingAppt] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user) return;

    const fetch = async () => {
      const [rxRes, orderRes, assessRes, apptRes] = await Promise.all([
        supabase
          .from("prescriptions")
          .select("id, peptide_name, dosage, status, created_at")
          .eq("patient_id", user.id)
          .order("created_at", { ascending: false })
          .limit(5),
        supabase
          .from("orders")
          .select("id, amount_cents, status, order_type, created_at, batch_number, coa_document_url")
          .eq("patient_id", user.id)
          .order("created_at", { ascending: false })
          .limit(5),
        supabase
          .from("health_assessments")
          .select("id")
          .eq("user_id", user.id)
          .limit(1),
        supabase
          .from("appointments")
          .select("id")
          .eq("patient_id", user.id)
          .in("status", ["scheduled", "confirmed"])
          .limit(1),
      ]);

      setPrescriptions(rxRes.data || []);
      setOrders(orderRes.data || []);
      setHasAssessment((assessRes.data || []).length > 0);
      setHasUpcomingAppt((apptRes.data || []).length > 0);
      setLoading(false);
    };

    fetch();
  }, [user]);

  if (loading) {
    return (
      <div className="flex items-center gap-2 text-muted-foreground py-4">
        <Loader2 size={16} className="animate-spin" /> Loading treatment info…
      </div>
    );
  }

  if (prescriptions.length === 0 && orders.length === 0) {
    // Friendly empty state — guide patient to the next concrete step
    const nextStep = !hasAssessment
      ? { label: "Start health assessment", path: "/get-started", icon: ClipboardList }
      : !hasUpcomingAppt
        ? { label: "Book your consultation", path: "/book", icon: CalendarDays }
        : { label: "View upcoming appointment", path: "/dashboard", icon: CalendarDays };
    const Icon = nextStep.icon;

    const headline = !hasAssessment
      ? "Your treatment plan starts here"
      : !hasUpcomingAppt
        ? "Almost there — book your consultation"
        : "Your provider will prescribe after your visit";

    const body = !hasAssessment
      ? "Once you complete a quick health assessment and meet with a licensed provider, your personalized peptide plan will appear here."
      : !hasUpcomingAppt
        ? "After your video consultation, any prescriptions and shipments your provider sends to the pharmacy will show up here."
        : "After your upcoming consultation, prescriptions and shipment tracking will appear here automatically.";

    return (
      <div className="mb-10">
        <h2 className="font-display text-lg text-foreground mb-4">Treatment & Orders</h2>
        <Card className="border-primary/15 bg-gradient-to-br from-primary/5 via-card to-secondary/30 overflow-hidden">
          <CardContent className="p-10 text-center">
            <div className="relative mx-auto mb-5 w-20 h-20">
              <div className="absolute inset-0 rounded-full bg-primary/10 animate-pulse" />
              <div className="relative w-20 h-20 rounded-full bg-primary/15 flex items-center justify-center ring-4 ring-primary/5">
                <Pill size={32} className="text-primary" strokeWidth={1.75} />
              </div>
              <Sparkles size={14} className="absolute -top-1 -right-1 text-primary/60" />
            </div>
            <h3 className="font-display text-xl text-foreground mb-2">{headline}</h3>
            <p className="text-muted-foreground text-sm mb-6 max-w-sm mx-auto leading-relaxed">
              {body}
            </p>
            <Button size="lg" onClick={() => navigate(nextStep.path)} className="gap-2 shadow-sm">
              <Icon size={16} />
              {nextStep.label}
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="mb-10">
      <h2 className="font-display text-lg text-foreground mb-4">Treatment & Orders</h2>
      <div className="space-y-3">
        {prescriptions.map((rx) => {
          const s = statusMap[rx.status] || { label: rx.status, variant: "outline" as const };
          return (
            <Card key={rx.id} className="border-border bg-card">
              <CardContent className="p-4 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 rounded-full bg-primary/10 flex items-center justify-center">
                    <Pill size={16} className="text-primary" />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-foreground">{rx.peptide_name}</p>
                    <p className="text-xs text-muted-foreground">{rx.dosage} · {new Date(rx.created_at).toLocaleDateString()}</p>
                  </div>
                </div>
                <Badge variant={s.variant}>{s.label}</Badge>
              </CardContent>
            </Card>
          );
        })}

        {orders.map((o) => {
          const s = statusMap[o.status] || { label: o.status, variant: "outline" as const };
          return (
            <Card key={o.id} className="border-border bg-card">
              <CardContent className="p-4 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 rounded-full bg-accent flex items-center justify-center">
                    <Package size={16} className="text-accent-foreground" />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-foreground">
                      {o.order_type === "subscription" ? "Subscription" : "Order"}: ${(o.amount_cents / 100).toFixed(2)}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {new Date(o.created_at).toLocaleDateString()}
                      {o.batch_number && <> · Lot {o.batch_number}</>}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {o.coa_document_url && (
                    <a
                      href={o.coa_document_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1 text-xs text-primary hover:underline"
                      title="Certificate of Analysis"
                    >
                      <FileDown size={14} />
                      COA
                    </a>
                  )}
                  <Badge variant={s.variant}>{s.label}</Badge>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>
    </div>
  );
};

export default TreatmentStatus;
